import { ApiBase, PageableResult } from '@bymason/api-base';
import { omit } from 'ramda';
import {
  CreateProvisonSessionOption,
  GetProvisionSessionOptions,
  GetProvisionSessionsOptions,
  ProvisionSession,
  ProvisionSessionDevice,
  ProvisionSessionInfo,
} from './types';
export * from './types';

class Provision extends ApiBase {
  private static instance: Provision;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns An Provision instance
   */
  public static getInstance(): Provision {
    if (!Provision.instance) {
      Provision.instance = new Provision();
    }

    return Provision.instance;
  }

  /**
   * Gets list of provision sessions.
   * @param options metadata required to retrive session data.
   * @param clientToken token required for proper authorization.
   * @param signal abort conroller signal for fetch
   * @returns either ProvisionSession or void
   */
  public async getSessions(
    options: GetProvisionSessionsOptions,
    clientToken: string,
    signal?: AbortSignal | null
  ) {
    const urlTemplate = this.getEndpointTemplate(`provision-session`);
    const url = this.expandUrlParams(urlTemplate, omit(['sessionId'], options), {
      addUnusedParamsToSearch: true,
    });
    const dataResponse = await this.callJsonEndpoint<PageableResult<ProvisionSessionInfo[]>>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
      signal,
    });

    return dataResponse;
  }

  /**
   * Gets provision session by id.
   * @param options metadata required to retrive session data.
   * @param clientToken token required for proper authorization.
   * @param signal abort conroller signal for fetch
   * @returns either ProvisionSession or void
   */
  public async getSession(
    options: GetProvisionSessionOptions,
    clientToken: string,
    signal?: AbortSignal | null
  ) {
    const urlTemplate = this.getEndpointTemplate(`provision-session/${options.sessionId}`);
    const url = this.expandUrlParams(urlTemplate, omit(['sessionId'], options), {
      addUnusedParamsToSearch: true,
    });
    const dataResponse = await this.callJsonEndpoint<PageableResult<ProvisionSession>>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
      signal,
    });

    return dataResponse;
  }

  /**
   * Gets provision session devices by id.
   * (This is a revision of the getSession endpoint which doesn't conform to the PageableResult pattern)
   * @param options metadata required to retrive session data.
   * @param clientToken token required for proper authorization.
   * @param signal abort conroller signal for fetch
   * @returns either ProvisionSession or void
   */
  public async getSessionDevices(
    options: GetProvisionSessionOptions,
    clientToken: string,
    signal?: AbortSignal | null
  ) {
    const urlTemplate = this.getEndpointTemplate(`provision-session/${options.sessionId}`);
    const url = this.expandUrlParams(urlTemplate, omit(['sessionId'], options), {
      addUnusedParamsToSearch: true,
    });
    const dataResponse = await this.callJsonEndpoint<PageableResult<ProvisionSession>>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
      signal,
    });

    return {
      data: dataResponse?.data?.devices,
      nextPage: dataResponse?.nextPage,
      prevPage: dataResponse?.prevPage,
    } as PageableResult<ProvisionSessionDevice[]>;
  }

  /**
   * Creates a provision session.
   * @param options metadata required to create a provison session.
   * @param clientToken token required for proper authorization.
   * @returns either ProvisionSession or void
   */
  public async newSession(options: CreateProvisonSessionOption, clientToken: string) {
    const url = this.getEndpointTemplate('provision-session');

    const dataResponse = (await this.callJsonEndpoint)<ProvisionSessionInfo>({
      body: JSON.stringify(options),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }
}

export const provisionClient = Provision.getInstance();
